import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const PERIODS = [
  { value: 'all', label: 'Tout' },
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Annee' },
  { value: 'custom', label: 'Periode' }
];

const formatCurrency = (value) => new Intl.NumberFormat('fr-TN', {
  style: 'currency',
  currency: 'TND',
  minimumFractionDigits: 3
}).format(Number(value || 0));

const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-';

const getId = (value) => value?._id || value || '';

const FournisseurPriceComparisonDashboard = ({ getApiUrl, types = [], onNavigate }) => {
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    produitId: '',
    typeProduitId: '',
    categorieId: '',
    periode: 'month',
    dateDebut: '',
    dateFin: ''
  });
  const [data, setData] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState('');
  const [expandedKey, setExpandedKey] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const [produitsRes, categoriesRes] = await Promise.all([
          axios.get(getApiUrl('/produits'), { headers: authHeaders() }),
          axios.get(getApiUrl('/categories'), { headers: authHeaders() })
        ]);
        setProduits(produitsRes.data?.data || produitsRes.data || []);
        setCategories(categoriesRes.data?.data || categoriesRes.data || []);
      } catch (err) {
        setError('Impossible de charger les options produits.');
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, [getApiUrl]);

  const filteredProducts = useMemo(() => {
    return produits.filter((produit) => {
      if (filters.typeProduitId && getId(produit.typeProduit) !== filters.typeProduitId) return false;
      if (filters.categorieId && getId(produit.categorie) !== filters.categorieId) return false;
      return true;
    });
  }, [produits, filters.typeProduitId, filters.categorieId]);

  const canCompare = Boolean(filters.produitId || filters.typeProduitId || filters.categorieId);

  const fetchComparison = async () => {
    if (!canCompare) {
      setError('Selectionnez un produit, un type ou une categorie.');
      return;
    }

    setLoadingComparison(true);
    setError('');
    setExpandedKey('');
    try {
      const params = new URLSearchParams();
      if (filters.produitId) params.append('produitId', filters.produitId);
      if (!filters.produitId && filters.typeProduitId) params.append('typeProduitId', filters.typeProduitId);
      if (!filters.produitId && filters.categorieId) params.append('categorieId', filters.categorieId);
      params.append('periode', filters.periode);
      if (filters.periode === 'custom') {
        if (filters.dateDebut) params.append('dateDebut', filters.dateDebut);
        if (filters.dateFin) params.append('dateFin', filters.dateFin);
      }

      const response = await axios.get(getApiUrl(`/fournisseurs/price-comparison?${params}`), {
        headers: authHeaders()
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la comparaison des prix.');
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'produitId' && value) {
        const produit = produits.find(p => p._id === value);
        next.typeProduitId = getId(produit?.typeProduit);
        next.categorieId = getId(produit?.categorie);
      }
      if ((name === 'typeProduitId' || name === 'categorieId') && value) {
        next.produitId = '';
      }
      return next;
    });
  };

  const createBonCommande = (row) => {
    onNavigate('produitsAdmin', {
      bc: '1',
      fournisseurId: row.fournisseur._id,
      produitId: row.produit._id
    });
  };

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  return (
    <div className="fpd-dashboard">
      <div className="fpd-hero">
        <div>
          <span>Comparaison achats</span>
          <h2>Prix fournisseurs par produit</h2>
          <p>Analyse les prix reels issus des BC, BL et factures fournisseurs pour aider au choix avant commande.</p>
        </div>
        <button className="fpd-primary" onClick={fetchComparison} disabled={!canCompare || loadingComparison}>
          <i className={`fas ${loadingComparison ? 'fa-spinner fa-spin' : 'fa-chart-line'}`}></i>
          Comparer
        </button>
      </div>

      <div className="fpd-filters">
        <label>
          <span>Produit exact</span>
          <select value={filters.produitId} onChange={(e) => handleFilterChange('produitId', e.target.value)} disabled={loadingOptions}>
            <option value="">Comparer par type/categorie</option>
            {filteredProducts.map(produit => (
              <option key={produit._id} value={produit._id}>
                {produit.nomProduit}{produit.reference ? ` - ${produit.reference}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Type produit</span>
          <select value={filters.typeProduitId} onChange={(e) => handleFilterChange('typeProduitId', e.target.value)}>
            <option value="">Tous</option>
            {types.map(type => <option key={type._id} value={type._id}>{type.label}</option>)}
          </select>
        </label>
        <label>
          <span>Categorie</span>
          <select value={filters.categorieId} onChange={(e) => handleFilterChange('categorieId', e.target.value)}>
            <option value="">Toutes</option>
            {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.label}</option>)}
          </select>
        </label>
        <label>
          <span>Periode historique</span>
          <select value={filters.periode} onChange={(e) => handleFilterChange('periode', e.target.value)}>
            {PERIODS.map(period => <option key={period.value} value={period.value}>{period.label}</option>)}
          </select>
        </label>
        {filters.periode === 'custom' && (
          <>
            <label>
              <span>Debut</span>
              <input type="date" value={filters.dateDebut} onChange={(e) => handleFilterChange('dateDebut', e.target.value)} />
            </label>
            <label>
              <span>Fin</span>
              <input type="date" value={filters.dateFin} onChange={(e) => handleFilterChange('dateFin', e.target.value)} />
            </label>
          </>
        )}
      </div>

      {error && <div className="fpd-error">{error}</div>}

      {data && (
        <>
          <div className="fpd-summary-grid">
            <div><span>Prix le moins cher</span><strong>{formatCurrency(summary.minPrice)}</strong></div>
            <div><span>Prix le plus cher</span><strong>{formatCurrency(summary.maxPrice)}</strong></div>
            <div><span>Prix moyen</span><strong>{formatCurrency(summary.avgPrice)}</strong></div>
            <div><span>Ecart</span><strong>{formatCurrency(summary.differenceDT)} · {summary.differencePercent || 0}%</strong></div>
            <div className="fpd-best"><span>Meilleur fournisseur</span><strong>{summary.bestSupplier?.raisonSociale || '-'}</strong></div>
          </div>

          <div className="fpd-table-wrap">
            <table className="fpd-table">
              <thead>
                <tr>
                  <th>Fournisseur</th>
                  <th>Produit / reference</th>
                  <th>Marque</th>
                  <th>Prix actuel</th>
                  <th>Dernier prix</th>
                  <th>Moyen / Min / Max</th>
                  <th>Delai</th>
                  <th>Dernier achat</th>
                  <th>Stock</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="fpd-empty">Aucune donnee fournisseur/prix trouvee pour cette selection.</td>
                  </tr>
                ) : rows.map((row) => {
                  const key = `${row.fournisseur._id}-${row.produit._id}`;
                  return (
                    <React.Fragment key={key}>
                      <tr className={row.isBestPrice ? 'fpd-row-best' : ''}>
                        <td>
                          <strong>{row.fournisseur.raisonSociale}</strong>
                          <small>{row.fournisseur.statut}</small>
                        </td>
                        <td>
                          <strong>{row.produit.nomProduit}</strong>
                          <small>{row.produit.reference || '-'}</small>
                        </td>
                        <td>{row.produit.marque || '-'}</td>
                        <td><strong>{formatCurrency(row.prixAchatActuel)}</strong></td>
                        <td>{row.dernierPrixAchat ? formatCurrency(row.dernierPrixAchat) : '-'}</td>
                        <td>
                          {formatCurrency(row.prixMoyen)}
                          <small>{formatCurrency(row.prixMinimum)} &gt; {formatCurrency(row.prixMaximum)}</small>
                        </td>
                        <td>{row.delaiLivraison || '-'}</td>
                        <td>{formatDate(row.dateDernierAchat)}</td>
                        <td>{row.disponibilite} {row.produit.unite}</td>
                        <td>
                          <div className="fpd-actions">
                            <button onClick={() => createBonCommande(row)} title="Creer un Bon de Commande">
                              <i className="fas fa-file-invoice"></i>
                            </button>
                            <button onClick={() => setExpandedKey(expandedKey === key ? '' : key)} title="Voir historique">
                              <i className="fas fa-clock"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedKey === key && (
                        <tr className="fpd-history-row">
                          <td colSpan="10">
                            <div className="fpd-history">
                              <strong>Historique des prix</strong>
                              {row.historique.length === 0 ? (
                                <p>Aucun achat historise sur la periode. Prix actuel issu de la fiche produit.</p>
                              ) : (
                                row.historique.map((item, index) => (
                                  <span key={`${key}-${index}`}>
                                    {formatDate(item.date)} · {item.source} {item.numero} · {formatCurrency(item.prixUnitaire)} · qte {item.quantite}
                                  </span>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default FournisseurPriceComparisonDashboard;
